'use client'

import React, { useRef, useState, useEffect, useCallback } from 'react'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { motion, useInView } from 'framer-motion'
import IPreferAirLogo from '@/Components/icons/IPreferAirLogo'
import RevinuLogo from '@/Components/icons/RevinuLogo'
import NigmabotLogo from '@/Components/icons/NigmabotLogo'
import {
    FaPlane,
    FaBrain,
    FaShieldAlt,
    FaChartLine,
    FaCheckCircle,
    FaTint,
    FaHeartbeat,
    FaMoneyBillWave,
} from 'react-icons/fa'

import TravelB2BImage from '/public/products/travel.png'
import integraAI from '/public/products/integra-ai.png'
import debtPulseImg from '/public/products/dp.png'
import secureFlow from '/public/products/sf.png'
import riskIntel from '/public/products/riskintels.png'
import aquatap from '/public/products/k9.png'
import revinuImg from '/public/products/revinu.png'
import nigmaBot from '/public/products/nigmabot.png'
import medAI from '/public/products/medai.png'

/* ─── Data ──────────────────────────────────────────────────────────── */

const categories = [
    'All',
    'Travel',
    'FinTech',
    'AI & ML',
    'HealthTech',
    'CleanTech',
]

const products = [
    /* ── Travel ── */
    {
        id: 0,
        title: 'iPreferAir',
        description:
            'A robust B2B travel booking platform offering real-time inventory, advanced management tools and seamless operations for travel agencies worldwide.',
        image: TravelB2BImage,
        href: '/products/b2b-travel',
        color: '#055bfa',
        tag: 'Travel Tech',
        category: 'Travel',
        icon: IPreferAirLogo,
        stats: [
            { label: 'Booking Speed', value: '3x Faster' },
            { label: 'API Integrations', value: '50+' },
        ],
    },
    /* ── FinTech ── */
    {
        id: 2,
        title: 'DebtPulse',
        description:
            'AI-powered digital collections platform for NBFCs and fintech lenders — prioritize high-risk accounts, automate compliant outreach, and boost recovery rates at scale.',
        image: debtPulseImg,
        href: '/products/debtpulse',
        color: '#058af0',
        tag: 'FinTech',
        category: 'FinTech',
        icon: FaMoneyBillWave,
        stats: [
            { label: 'Recovery Boost', value: '35%' },
            { label: 'Accounts Managed', value: '1M+' },
        ],
    },
    {
        id: 3,
        title: 'SecureFlow',
        description:
            'A high-performance fraud detection pipeline that processes millions of transactions in real time with sub-millisecond latency — keeping your revenue safe around the clock.',
        image: secureFlow,
        href: '/products/secure-flow',
        color: '#172554',
        tag: 'Cybersecurity',
        category: 'FinTech',
        icon: FaShieldAlt,
        stats: [
            { label: 'Detection Speed', value: '<1ms' },
            { label: 'Accuracy Rate', value: '99.7%' },
        ],
    },
    {
        id: 4,
        title: 'RiskIntel',
        description:
            'A comprehensive risk management platform that helps businesses identify, assess and mitigate risks with data-driven precision — from credit scoring to portfolio analysis.',
        image: riskIntel,
        href: '/products/riskintel',
        color: '#055bfa',
        tag: 'Risk Management',
        category: 'FinTech',
        icon: FaChartLine,
        stats: [
            { label: 'Risk Models', value: '15+' },
            { label: 'Faster Assessment', value: '60%' },
        ],
    },
    {
        id: 7,
        title: 'Revinu',
        description:
            'Intelligent payment recovery platform that detects failed payments, categorizes root causes, and triggers smart retries automatically — recovering revenue you thought was lost.',
        image: revinuImg,
        href: '/products/revinu',
        color: '#055bfa',
        tag: 'Payment Recovery',
        category: 'FinTech',
        icon: RevinuLogo,
        stats: [
            { label: 'Recovery Rate', value: '40%+' },
            { label: 'Retry Logic', value: 'AI-Powered' },
        ],
    },

    /* ── AI & ML ── */
    {
        id: 1,
        title: 'Integra-AI',
        description:
            'A unified AI chat platform with a clean, conversation-style interface. Bring your own API keys and connect to leading models like GPT-4, Claude and DeepSeek — all in one place.',
        image: integraAI,
        href: '/products/integra-ai',
        color: '#172554',
        tag: 'Artificial Intelligence',
        category: 'AI & ML',
        icon: FaBrain,
        stats: [
            { label: 'Models Supported', value: '10+' },
            { label: 'Avg Response', value: '<2s' },
        ],
    },
    {
        id: 8,
        title: 'Nigmabot AI',
        description:
            'Your AI-powered companion for smarter, seamless, customized business solutions — streamlining operations, automating tasks, and driving growth with cutting-edge innovation.',
        image: nigmaBot,
        href: '/products/nigmabot-ai',
        color: '#058af0',
        tag: 'AI Automation',
        category: 'AI & ML',
        icon: NigmabotLogo,
        stats: [
            { label: 'Tasks Automated', value: '500+' },
            { label: 'Uptime', value: '99.9%' },
        ],
    },
    {
        id: 9,
        title: 'MedAI',
        description:
            'AI-powered medical diagnosis that combines intelligent architecture with compassionate care. Experience the next generation of healthcare intelligence for better outcomes.',
        image: medAI,
        href: 'https://medai.cloudastra.co/',
        target: '_blank',
        color: '#172554',
        tag: 'HealthTech',
        category: 'HealthTech',
        icon: FaHeartbeat,
        stats: [
            { label: 'Diagnostic Accuracy', value: '95%+' },
            { label: 'Conditions Covered', value: '300+' },
        ],
    },
    /* ── CleanTech ── */
    {
        id: 6,
        title: 'Aquaflowz',
        description:
            'AI-powered water quality monitoring and management system that ensures safe and clean water through real-time analysis, predictive insights, and automated alerting.',
        image: aquatap,
        href: 'https://aquaflowz.cloudastra.co/',
        target: '_blank',
        color: '#172554',
        tag: 'CleanTech',
        category: 'CleanTech',
        icon: FaTint,
        stats: [
            { label: 'Sensors Supported', value: '200+' },
            { label: 'Real-time Alerts', value: '24/7' },
        ],
    },
]

const impactStats = [
    { value: 10, suffix: '+', label: 'Products Shipped' },
    { value: 7, suffix: '+', label: 'Industries Served' },
    { value: 99, suffix: '.9%', label: 'Platform Uptime' },
    { value: 50, suffix: '+', label: 'Enterprise Clients' },
]

/* ─── Animated Counter ──────────────────────────────────────────────── */

function AnimatedCounter({ target, suffix = '' }) {
    const [count, setCount] = useState(0)
    const ref = useRef(null)
    const inView = useInView(ref, { once: true, margin: '-50px' })

    useEffect(() => {
        if (!inView) return
        let start = 0
        const duration = 1800
        const startTime = performance.now()

        function step(now) {
            const elapsed = now - startTime
            const progress = Math.min(elapsed / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3)
            setCount(Math.floor(eased * target))
            if (progress < 1) requestAnimationFrame(step)
        }

        requestAnimationFrame(step)
    }, [inView, target])

    return (
        <span ref={ref} className="tabular-nums">
            {count}{suffix}
        </span>
    )
}

/* ─── Product Card ──────────────────────────────────────────────────── */

function ProductCard({ product, index }) {
    const ref = useRef(null)
    const inView = useInView(ref, { once: true, margin: '-80px' })
    const isEven = index % 2 === 0
    const isExternal = product.href.startsWith('http')
    const LinkWrapper = isExternal ? 'a' : Link
    const linkProps = isExternal
        ? { href: product.href, target: '_blank', rel: 'noopener noreferrer' }
        : { href: product.href }
    const Icon = product.icon

    return (
        <motion.article
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="group"
        >
            <div
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0 rounded-2xl overflow-hidden bg-gradient-to-br from-white via-[#f4f7ff] to-[#eaf0ff] border border-[#055bfa]/15 shadow-lg shadow-[#055bfa]/5 hover:border-[#055bfa]/40 hover:shadow-xl hover:shadow-[#055bfa]/10 transition-all duration-300`}
            >
                {/* Image Side */}
                <div className="relative w-full lg:w-[55%] h-[280px] sm:h-[340px] lg:h-[440px] overflow-hidden">
                    <div
                        className="absolute inset-0 z-10 opacity-40"
                        style={{
                            background: `linear-gradient(${isEven ? 'to right' : 'to left'}, transparent 60%, ${product.color}40 100%)`,
                        }}
                    />
                    <Image
                        src={product.image}
                        alt={`${product.title} — ${product.tag} product by CloudAstra`}
                        fill
                        className="object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        sizes="(max-width: 1024px) 100vw, 55vw"
                        placeholder="blur"
                    />
                </div>

                {/* Content Side */}
                <div className="w-full lg:w-[45%] p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                        <span
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg"
                            style={{ backgroundColor: product.color }}
                        >
                            <Icon size={22} />
                        </span>
                        <span className="text-xs font-semibold tracking-widest uppercase text-[#058af0]">
                            {product.tag}
                        </span>
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 group-hover:text-[#055bfa] transition-colors duration-300">
                        {product.title}
                    </h3>

                    <p className="text-gray-500 leading-relaxed text-sm sm:text-base mb-6">
                        {product.description}
                    </p>

                    {/* Mini Stats */}
                    <div className="flex gap-6 mb-6">
                        {product.stats.map((stat, i) => (
                            <div key={i} className="flex flex-col">
                                <span className="text-lg sm:text-xl font-bold text-gray-900">
                                    {stat.value}
                                </span>
                                <span className="text-xs text-gray-400 mt-0.5">
                                    {stat.label}
                                </span>
                            </div>
                        ))}
                    </div>

                    <LinkWrapper
                        {...linkProps}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#055bfa] hover:bg-[#058af0] px-6 py-3 rounded-lg w-fit transition-colors duration-200"
                    >
                        View Case Study
                        <svg
                            className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </LinkWrapper>
                </div>
            </div>
        </motion.article>
    )
}

/* ─── Industry Badge ────────────────────────────────────────────────── */

const industries = [
    { name: 'FinTech & Banking', icon: FaMoneyBillWave },
    { name: 'Healthcare', icon: FaHeartbeat },
    { name: 'Travel & Hospitality', icon: FaPlane },
    { name: 'Cybersecurity', icon: FaShieldAlt },
    { name: 'AI & Machine Learning', icon: FaBrain },
    { name: 'Clean Energy', icon: FaTint },
    { name: 'Compliance & RegTech', icon: FaCheckCircle },
]

/* ─── Process Section ───────────────────────────────────────────────── */

const processSteps = [
    {
        step: '01',
        title: 'Discovery & Strategy',
        desc: 'Deep-dive into business requirements, user personas, market landscape, and technical constraints.',
        details: [
            'Stakeholder interviews & workshops',
            'Competitive analysis & market research',
            'Technical feasibility assessment',
            'Project roadmap & milestone planning',
        ],
    },
    {
        step: '02',
        title: 'Architecture & Design',
        desc: 'Scalable system design, intuitive UX wireframes, and technology selection aligned with your goals.',
        details: [
            'System architecture & data modeling',
            'UI/UX wireframes & prototypes',
            'Technology stack selection',
            'Security & compliance planning',
        ],
    },
    {
        step: '03',
        title: 'Agile Development',
        desc: 'Iterative sprints with continuous integration, automated testing, and transparent progress tracking.',
        details: [
            '2-week sprint cycles with demos',
            'CI/CD pipeline & automated testing',
            'Code reviews & quality gates',
            'Real-time progress dashboards',
        ],
    },
    {
        step: '04',
        title: 'Launch & Scale',
        desc: 'Production deployment, performance optimization, monitoring setup, and ongoing iteration.',
        details: [
            'Zero-downtime deployment',
            'Performance & load testing',
            '24/7 monitoring & alerting',
            'Post-launch iteration & support',
        ],
    },
]

function ProcessSection() {
    const [activeStep, setActiveStep] = useState(0)
    const [isPaused, setIsPaused] = useState(false)
    const sectionRef = useRef(null)
    const sectionInView = useInView(sectionRef, { once: false, margin: '-100px' })

    useEffect(() => {
        if (!sectionInView || isPaused) return
        const timer = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % processSteps.length)
        }, 4000)
        return () => clearInterval(timer)
    }, [sectionInView, isPaused])

    const handleStepClick = useCallback((i) => {
        setActiveStep(i)
        setIsPaused(true)
        setTimeout(() => setIsPaused(false), 8000)
    }, [])

    return (
        <section ref={sectionRef} className="w-full bg-gradient-to-b from-[#eef2ff] via-[#f0f4ff] to-[#f8fafc] py-20 lg:py-28 relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#055bfa] rounded-full blur-[300px] opacity-[0.06]" />
            <div className="absolute top-[-100px] right-[-100px] w-[400px] h-[400px] bg-[#058af0] rounded-full blur-[200px] opacity-[0.04]" />

            <div className="relative z-10 max-w-[1750px] mx-auto px-6">
                <div className="text-center mb-16">
                    <motion.span
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                        className="inline-block text-sm font-semibold tracking-widest uppercase text-[#055bfa] mb-4"
                    >
                        Our Process
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
                    >
                        How We Build Products
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                        className="text-gray-500 text-lg max-w-2xl mx-auto"
                    >
                        A proven process that takes ideas from concept to market-ready
                        products with speed and precision.
                    </motion.p>
                </div>

                {/* Timeline Navigation */}
                <div className="relative mb-12">
                    {/* Connecting line */}
                    <div className="hidden lg:block absolute top-6 left-0 right-0 h-[2px] bg-gray-200" />
                    <div
                        className="hidden lg:block absolute top-6 left-0 h-[2px] bg-gradient-to-r from-[#055bfa] to-[#058af0] transition-all duration-500 ease-out"
                        style={{ width: `${((activeStep + 1) / processSteps.length) * 100}%` }}
                    />

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-0">
                        {processSteps.map((item, i) => (
                            <motion.button
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.1 }}
                                onClick={() => handleStepClick(i)}
                                className="relative flex flex-col items-center lg:items-center text-center group cursor-pointer"
                            >
                                {/* Step circle */}
                                <div
                                    className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 relative z-10 ${
                                        i <= activeStep
                                            ? 'bg-gradient-to-br from-[#055bfa] to-[#058af0] text-white shadow-lg shadow-[#055bfa]/25 scale-110'
                                            : 'bg-white text-gray-400 border border-gray-200 shadow-sm group-hover:border-[#055bfa]/40 group-hover:text-gray-600'
                                    }`}
                                >
                                    {item.step}
                                </div>
                                {/* Step title */}
                                <span
                                    className={`mt-3 text-sm font-semibold transition-colors duration-300 ${
                                        i === activeStep ? 'text-gray-900' : 'text-gray-400 group-hover:text-gray-600'
                                    }`}
                                >
                                    {item.title}
                                </span>
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* Active Step Content */}
                <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="relative bg-white border border-gray-200/80 rounded-2xl overflow-hidden shadow-xl shadow-[#055bfa]/[0.04]"
                >
                    

                    <div className="flex flex-col lg:flex-row">
                        {/* Left — description */}
                        <div className="flex-1 p-8 lg:p-12">
                            <div className="flex items-center gap-4 mb-6">
                                
                                <span className="text-6xl font-black bg-gradient-to-br from-[#055bfa] to-[#058af0] bg-clip-text text-transparent">
                                    {processSteps[activeStep].step}
                                </span>
                                <div>
                                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                                        {processSteps[activeStep].title}
                                    </h3>
                                </div>
                            </div>
                            <p className="text-gray-500 text-base lg:text-lg leading-relaxed max-w-xl">
                                {processSteps[activeStep].desc}
                            </p>
                        </div>

                        {/* Right — detail checklist */}
                        <div className="flex-1 p-8 lg:p-12 lg:border-l border-t lg:border-t-0 border-gray-100 bg-gradient-to-br from-[#f0f4ff] to-[#f8fafc]">
                            <p className="text-xs font-semibold uppercase tracking-widest text-[#055bfa] mb-4">
                                Key Activities
                            </p>
                            <ul className="space-y-4">
                                {processSteps[activeStep].details.map((detail, j) => (
                                    <motion.li
                                        key={j}
                                        initial={{ opacity: 0, x: 15 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, delay: j * 0.08 }}
                                        className="flex items-start gap-3"
                                    >
                                        <FaCheckCircle className="text-[#055bfa] text-sm mt-1 flex-shrink-0" />
                                        <span className="text-gray-600 text-sm lg:text-base">{detail}</span>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Step navigation */}
                    <div className="flex items-center justify-between px-8 lg:px-12 py-5 border-t border-gray-100">
                        <button
                            onClick={() => handleStepClick(Math.max(0, activeStep - 1))}
                            disabled={activeStep === 0}
                            className="text-sm font-semibold text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                            </svg>
                            Previous
                        </button>
                        <div className="flex gap-2">
                            {processSteps.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => handleStepClick(i)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                        i === activeStep
                                            ? 'w-6 bg-gradient-to-r from-[#055bfa] to-[#058af0]'
                                            : 'bg-gray-200 hover:bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>
                        <button
                            onClick={() => handleStepClick(Math.min(processSteps.length - 1, activeStep + 1))}
                            disabled={activeStep === processSteps.length - 1}
                            className="text-sm font-semibold text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            Next
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    )
}

/* ─── Main Page ─────────────────────────────────────────────────────── */

export default function Products() {
    const [activeCategory, setActiveCategory] = useState('All')
    const filterScrollRef = useRef(null)

    // Auto-scroll filter tabs to active category on mobile
    useEffect(() => {
        const container = filterScrollRef.current
        if (!container) return
        const activeBtn = container.querySelector(`[data-category="${activeCategory}"]`)
        if (!activeBtn) return
        const scrollLeft = activeBtn.offsetLeft - container.offsetWidth / 2 + activeBtn.offsetWidth / 2
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
    }, [activeCategory])

    const filtered =
        activeCategory === 'All'
            ? products
            : products.filter((p) => p.category === activeCategory)

    return (
        <>
            <Head>
                <title>Our Products | CloudAstra — AI-Powered Software Solutions</title>
                <meta
                    name="description"
                    content="Explore CloudAstra's suite of 10+ innovative products — from AI-powered platforms and fraud detection to travel booking, compliance automation, and healthcare intelligence."
                />
                <meta
                    name="keywords"
                    content="CloudAstra products, iPreferAir, Integra-AI, DebtPulse, SecureFlow, RiskIntel, Compliance, Aquaflowz, Revinu, Nigmabot AI, MedAI, AI solutions, fintech, healthtech, travel tech, SaaS products"
                />
                <meta property="og:title" content="Our Products | CloudAstra" />
                <meta
                    property="og:description"
                    content="Discover CloudAstra's innovative product suite — AI platforms, fraud detection, travel booking, compliance automation, and more."
                />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Our Products | CloudAstra" />
                <meta
                    name="twitter:description"
                    content="10+ AI-powered products across FinTech, HealthTech, Travel, Cybersecurity and more."
                />

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'CollectionPage',
                            name: 'CloudAstra Products',
                            description:
                                'Explore CloudAstra\'s suite of innovative AI-powered software products.',
                            url: 'https://cloudastra.co/products',
                            mainEntity: {
                                '@type': 'ItemList',
                                numberOfItems: products.length,
                                itemListElement: products.map((p, i) => ({
                                    '@type': 'ListItem',
                                    position: i + 1,
                                    name: p.title,
                                    description: p.description,
                                    url: p.href.startsWith('http')
                                        ? p.href
                                        : `https://cloudastra.co${p.href}`,
                                })),
                            },
                        }),
                    }}
                />
            </Head>

            <main className="bg-[#f8fafc]">
                {/* ── Hero ─────────────────────────────────────────── */}
                <section className="relative w-full  flex items-center overflow-hidden bg-[#F8FAFC]">
                    {/* Background layers */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#055bfa06_1px,transparent_1px),linear-gradient(to_bottom,#055bfa06_1px,transparent_1px)] bg-[size:64px_64px]" />
                    <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-[#055bfa] rounded-full blur-[260px] opacity-[0.06]" />
                    <div className="absolute bottom-[-100px] right-[-100px] w-[500px] h-[500px] bg-[#058af0] rounded-full blur-[200px] opacity-[0.05]" />

                    <div className="relative z-10 w-full max-w-[1750px] mx-auto px-6 py-32 lg:py-40">
                        <div className="max-w-7xl mx-auto text-center">
                            <motion.span
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className="inline-block text-sm font-semibold tracking-widest uppercase text-[#058af0] mb-5"
                            >
                                Product Portfolio
                            </motion.span>

                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.1 }}
                                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl  font-bold text-gray-900 leading-[1.1] tracking-tight"
                            >
                                Products That{' '}
                                <br className="sm:hidden" />
                                <span className="bg-gradient-to-r from-[#058af0] to-[#055bfa] bg-clip-text text-transparent px-2">
                                    Drive Results
                                </span>
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.2 }}
                                className="mt-6 text-lg md:text-xl text-gray-500 leading-relaxed max-w-xl mx-auto"
                            >
                                Enterprise-grade platforms powered by AI, built for scale, and
                                trusted by businesses across FinTech, Healthcare, Travel,
                                Cybersecurity, and beyond.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.3 }}
                                className="mt-10 flex flex-wrap justify-center gap-4"
                            >
                                <Link
                                    href="/contact"
                                    className="inline-flex items-center gap-2 bg-[#055bfa] hover:bg-[#058af0] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors text-sm"
                                >
                                    Discuss Your Project
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </Link>
                                <a
                                    href="#products"
                                    className="inline-flex items-center gap-2 border border-gray-300 hover:border-[#055bfa]/50 text-gray-600 hover:text-[#055bfa] font-semibold px-7 py-3.5 rounded-xl transition-colors text-sm"
                                >
                                    Explore Products
                                </a>
                            </motion.div>
                        </div>

                        {/* Hero Stats Strip */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            className="mt-16 lg:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-3xl mx-auto"
                        >
                            {impactStats.map((stat, i) => (
                                <div key={i} className="border-l-2 border-[#055bfa]/40 pl-4">
                                    <div className="text-3xl md:text-4xl font-bold text-gray-900">
                                        <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                                    </div>
                                    <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </section>

                {/* ── Category Filter + Products ───────────────────── */}
                <section id="products" className="w-full bg-[#f8fafc] py-20 lg:py-28">
                    <div className="max-w-[1750px] mx-auto px-6">
                        {/* Section Header */}
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                                What We Have Built
                            </h2>
                            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                                Each product is designed to solve real-world problems with
                                intelligent automation, modern architecture, and delightful user
                                experiences.
                            </p>
                        </div>

                        {/* Filter Tabs – horizontally scrollable on mobile */}
                        <div
                            ref={filterScrollRef}
                            className="flex lg:flex-wrap lg:justify-center gap-3 mb-14 overflow-x-auto lg:overflow-x-visible no-scrollbar snap-x snap-mandatory pb-2 -mx-6 px-6"
                        >
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    data-category={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`relative shrink-0 snap-center px-7 py-2.5 rounded-full text-base font-semibold tracking-wide transition-all duration-300 overflow-hidden ${
                                        activeCategory === cat
                                            ? 'bg-gradient-to-r from-[#055bfa] to-[#058af0] text-white shadow-lg shadow-[#055bfa]/20 scale-105'
                                            : 'bg-white text-gray-500 border border-gray-200 hover:border-[#055bfa]/50 hover:text-[#055bfa] hover:bg-blue-50/50 hover:scale-[1.03]'
                                    }`}
                                >
                                    {activeCategory === cat && (
                                        <span className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent rounded-full" />
                                    )}
                                    <span className="relative">{cat}</span>
                                </button>
                            ))}
                        </div>

                        {/* Product Cards */}
                        <div className="flex flex-col gap-10 lg:gap-14">
                            {filtered.map((product, i) => (
                                <ProductCard key={product.id} product={product} index={i} />
                            ))}
                        </div>

                        {filtered.length === 0 && (
                            <p className="text-center text-gray-400 py-20 text-lg">
                                No products found in this category.
                            </p>
                        )}
                    </div>
                </section>

                {/* ── Industries Section ────────────────────────────── */}
                <section className="w-full bg-gradient-to-b from-[#0f1a2e] via-[#152243] to-[#1a2d52] py-20 lg:py-28 relative overflow-hidden">
                    <div className="absolute top-[-150px] right-[-150px] w-[500px] h-[500px] bg-[#055bfa] rounded-full blur-[250px] opacity-[0.12]" />
                    <div className="absolute bottom-[-100px] left-[-100px] w-[400px] h-[400px] bg-[#058af0] rounded-full blur-[200px] opacity-[0.10]" />

                    <div className="relative z-10 max-w-[1750px] mx-auto px-6">
                        <div className="text-center mb-14">
                            <motion.span
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4 }}
                                className="inline-block text-sm font-semibold tracking-widest uppercase text-[#4d94ff] mb-4"
                            >
                                Cross-Industry Expertise
                            </motion.span>
                            <motion.h2
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.1 }}
                                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5"
                            >
                                Industries We Serve
                            </motion.h2>
                            <motion.p
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.2 }}
                                className="text-gray-400 leading-relaxed text-lg max-w-2xl mx-auto"
                            >
                                Our product expertise spans multiple verticals. We understand
                                the unique challenges of each industry and build solutions
                                that deliver measurable impact.
                            </motion.p>
                        </div>

                        {/* Row 1 — 4 cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-5">
                            {industries.slice(0, 4).map((ind, i) => {
                                const IndIcon = ind.icon
                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 25 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: '-40px' }}
                                        transition={{ duration: 0.4, delay: i * 0.07 }}
                                        className="group flex flex-col items-center gap-4 bg-white/5 border border-white/10 rounded-2xl px-5 py-7 hover:border-[#055bfa]/50 hover:bg-white/10 hover:shadow-lg hover:shadow-[#055bfa]/10 transition-all duration-300 hover:-translate-y-1"
                                    >
                                        <div className="w-16 h-16 rounded-xl bg-[#055bfa]/15 flex items-center justify-center group-hover:bg-[#055bfa]/25 transition-all duration-300">
                                            <IndIcon className="text-[#4d94ff] text-4xl group-hover:text-[#6aabff] transition-colors duration-300" />
                                        </div>
                                        <span className="text-base text-gray-300 font-semibold text-center group-hover:text-white transition-colors duration-300">
                                            {ind.name}
                                        </span>
                                    </motion.div>
                                )
                            })}
                        </div>

                        {/* Row 2 — 3 cards centered */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 sm:w-3/4 mx-auto">
                            {industries.slice(4).map((ind, i) => {
                                const IndIcon = ind.icon
                                return (
                                    <motion.div
                                        key={i + 4}
                                        initial={{ opacity: 0, y: 25 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: '-40px' }}
                                        transition={{ duration: 0.4, delay: (i + 4) * 0.07 }}
                                        className="group flex flex-col items-center gap-4 bg-white/5 border border-white/10 rounded-2xl px-5 py-7 hover:border-[#055bfa]/50 hover:bg-white/10 hover:shadow-lg hover:shadow-[#055bfa]/10 transition-all duration-300 hover:-translate-y-1"
                                    >
                                        <div className="w-14 h-14 rounded-xl bg-[#055bfa]/15 flex items-center justify-center group-hover:bg-[#055bfa]/25 transition-all duration-300">
                                            <IndIcon className="text-[#4d94ff] text-4xl group-hover:text-[#6aabff] transition-colors duration-300" />
                                        </div>
                                        <span className="text-sm text-gray-300 font-semibold text-center group-hover:text-white transition-colors duration-300">
                                            {ind.name}
                                        </span>
                                    </motion.div>
                                )
                            })}
                        </div>
                    </div>
                </section>

                {/* ── Process Section ───────────────────────────────── */}
                <ProcessSection />

                {/* ── Impact Stats Banner ───────────────────────────── */}
                <section className="w-full bg-gradient-to-r from-[#055bfa] to-[#058af0] py-16">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                            {[
                                { value: 10, suffix: '+', label: 'Live Products' },
                                { value: 500, suffix: 'K+', label: 'End Users Served' },
                                { value: 35, suffix: '%', label: 'Avg Efficiency Gain' },
                                { value: 99, suffix: '.9%', label: 'Uptime SLA' },
                            ].map((stat, i) => (
                                <div key={i}>
                                    <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
                                        <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                                    </div>
                                    <div className="text-sm text-white/70 mt-2 font-medium">
                                        {stat.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── CTA Section ──────────────────────────────────── */}
                <section className="w-full bg-[#172554] py-24 px-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#055bfa] rounded-full blur-[200px] opacity-10" />
                    <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#058af0] rounded-full blur-[160px] opacity-10" />

                    <div className="relative z-10 max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                            Have a Product Idea?
                            <br />
                            <span className="text-[#058af0]">Let&apos;s Build It Together.</span>
                        </h2>
                        <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                            Whether you need a custom platform, want to extend an existing product,
                            or are exploring AI integration — our team is ready to help you ship
                            faster.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link
                                href="/contact"
                                className="inline-flex items-center gap-2 bg-[#055bfa] hover:bg-[#058af0] text-white font-semibold px-8 py-4 rounded-xl transition-colors text-base"
                            >
                                Start a Conversation
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </Link>
                            <Link
                                href="/mvp-development-services"
                                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white/80 hover:text-white font-semibold px-8 py-4 rounded-xl transition-colors text-base"
                            >
                                Build Your MVP
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
        </>
    )
}
